<template>
  <div class="settings fixed-heading">
    <div class="heading" style="display: flex; flex-direction: row; justify-content: space-between">
      <div clas>
        <h1>{{ title }}</h1>
        <p class="guide_text">{{ info }}</p>
      </div>

      <div style="display: flex; gap: 1rem; align-items: center;">
        <simple-portfolio-dropdown
          v-model="selectedPortfolio"
          @update:modelValue="updatePortfolio"
        />
        <timeframe-dropdown
          :model-value="selectedTimeframe"
          @update:model-value="updateTimeframe"
        />
      </div>
    </div>

    <div class="economic-readiness-dashboard-body">
      <div class="economic-readiness-kpi-container">
        <div v-if="isLoadingKPIs" class="economic-readiness-loading">
          <Loading/>
        </div>

        <div v-else class="economic-readiness-kpi-grid">
          <FacetCountCard
            v-for="(kpi, index) in kpis"
            :key="'kpi_' + index"
            :facet-name="'kpi'"
            :value="kpi.value"
            :label="kpi.name"
            :count="kpi.value"
            :subtext="kpi.description"
            :width="'200px'"
            outlined-left
          />
        </div>
      </div>

      <ChartContainer
        :is-loading="isLoadingStatusHistogram"
        :has-data="!!statusHistogramData?.labels?.length"
        title="Organisations founded, stopped and acquired"
      >
        <BarChart
          v-if="statusHistogramData"
          :data="statusHistogramData"
          :stacked="true"
          :download-file-name="'status-histogram'"
          class="status-histogram-chart"
        />
      </ChartContainer>

      <ChartContainer
        :is-loading="isLoadingActiveActorsHistogram"
        :has-data="!!activeActorsHistogramData?.labels?.length"
        title="Active actors over time"
      >
        <LineChart
          v-if="activeActorsHistogramData"
          :data="activeActorsHistogramData"
          :download-file-name="'active-actors-over-time'"
          class="status-histogram-chart"
        />
      </ChartContainer>

      <ChartContainer
        :is-loading="isLoadingFundingHistogram"
        :has-data="!!fundingHistogramData?.labels?.length"
        title="Funding raised"
      >
        <BarChart
          v-if="fundingHistogramData"
          :data="fundingHistogramData"
          :money-formatted="true"
          :y-step-size="10"
          :show-value-counts="true"
          :download-file-name="'funding-raised-over-time'"
          value-counts-label="organization(s)"
        />
      </ChartContainer>

      <ChartContainer
        :is-loading="isLoadingFundingRoundTypes"
        :has-data="!!fundingRoundTypesData?.labels?.length"
        title="Funding round types"
      >
        <BarChart
          v-if="fundingRoundTypesData"
          :data="fundingRoundTypesData"
          :stacked="true"
          :download-file-name="'funding-round-types'"
          class="funding-round-types-chart"
        />
      </ChartContainer>

      <ChartContainer
        :is-loading="isLoadingRevenueHistogram"
        :has-data="!!revenueHistogramData?.labels?.length"
        title="Revenue over time"
      >
        <BarChart
          v-if="revenueHistogramData"
          :data="revenueHistogramData"
          :money-formatted="true"
          :y-step-size="10"
          :show-value-counts="true"
          :download-file-name="'revenue-over-time'"
          value-counts-label="companies"
        />
      </ChartContainer>

      <ChartContainer
        :is-loading="isLoadingDeclaredRevenueHistogram"
        :has-data="!!declaredRevenueHistogramData?.labels?.length"
        title="Declared Revenue over time"
      >
        <BarChart
          v-if="declaredRevenueHistogramData"
          :data="declaredRevenueHistogramData"
          :money-formatted="true"
          :y-step-size="10"
          :show-value-counts="true"
          :download-file-name="'declared-revenue-over-time'"
          value-counts-label="companies"
        />
      </ChartContainer>

      <ChartContainer
        :is-loading="isLoadingEmployeeHistogram"
        :has-data="!!employeeHistogramData?.labels?.length"
        title="Employees over time"
      >
        <BarChart
          v-if="employeeHistogramData"
          :data="employeeHistogramData"
          :show-value-counts="true"
          :download-file-name="'employees-over-time'"
          value-counts-label="organization(s)"
        />
      </ChartContainer>

      <ChartContainer
        :is-loading="isLoadingDeclaredEmployeeHistogram"
        :has-data="!!declaredEmployeeHistogramData?.labels?.length"
        title="Declared Employees over time"
      >
        <BarChart
          v-if="declaredEmployeeHistogramData"
          :data="declaredEmployeeHistogramData"
          :show-value-counts="true"
          :download-file-name="'declared-employees-over-time'"
          value-counts-label="organization(s)"
        />
      </ChartContainer>

      <div class="economic-readiness-chart-container">
        <div v-if="isLoadingFundingAndGrantRounds" class="economic-readiness-loading">
          <Loading/>
        </div>
        <template v-else>
          <div v-if="!fundingAndGrantRounds.length" class="economic-readiness-empty-state">
            No funding or grant rounds data available
          </div>
          <div v-else>
            <data-table
              :headers="fundingAndGrantRoundsHeaders"
              :data="fundingAndGrantRounds"
              :title="'Funding and Grants'"
              :download-file-name="'funding-and-grants'"
              @row-click="handleRowClick"
            >
              <template #logo="{ value, row }">
                <avatar
                  :src="value"
                  :alt="row.name"
                  variant="border"
                />
              </template>

              <template #date="{ value }">
                {{ formatDate(value) }}
              </template>
            </data-table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import TimeframeDropdown from '../../Dashboard/TimeframeDropdown.vue'
import SimplePortfolioDropdown from '../../Dashboard/SimplePortfolioDropdown.vue'
import FacetCountCard from '../../Dashboard/FacetCountCard.vue'
import DataTable from '../../Table/DataTable.vue'
import {
  fetchEconomicReadinessFundingHistogram,
  fetchEconomicReadinessKPIs,
  fetchEconomicReadinessStatusHistogram,
  fetchEconomicReadinessFundingRoundTypeHistogram,
  fetchFundingAndGrantRounds,
  fetchEconomicReadinessRevenueHistogram,
  fetchEconomicReadinessDeclaredRevenueHistogram,
  fetchEconomicReadinessEmployeeHistogram,
  fetchEconomicReadinessDeclaredEmployeeHistogram,
  fetchEconomicReadinessActiveActorsHistogram,
} from '@/api/analytics'
import Loading from '@/components/Dashboard/ConceptMap/Loading.vue'
import {
  transformKPIsResponseToKPIArray,
  transformChartDataApiResponseToChartDataArray,
  transformFundingOrGrantRoundListDataToFundingOrGrantRoundArray
} from '@/Domain/Analytics/Api/analytics-api-transformers'
import BarChart from '@/Domain/Analytics/Components/BarChart.vue'
import type { ChartData as AnalyticsChartData, EconomicReadinessKPIs } from '@/Domain/Analytics/analytics-types.d'
import Avatar from '@/components/Avatar/Avatar.vue'
import ChartContainer from './ChartContainer.vue'
import LineChart from '@/Domain/Analytics/Components/LineChart.vue'

interface BaseComponentData {
  selectedPortfolio: string | null
  selectedTimeframe: {
    label: string
    value: {
      year: number
      quarter: number | null
    }
  },
  isLoadingKPIs: boolean,
  isLoadingStatusHistogram: boolean,
  isLoadingFundingHistogram: boolean,
  isLoadingFundingRoundTypes: boolean,
  isLoadingFundingAndGrantRounds: boolean,
  isLoadingRevenueHistogram: boolean,
  isLoadingDeclaredRevenueHistogram: boolean,
  isLoadingEmployeeHistogram: boolean,
  isLoadingDeclaredEmployeeHistogram: boolean,
  isLoadingActiveActorsHistogram: boolean,
  kpis: EconomicReadinessKPIs | null,
  statusHistogramData: AnalyticsChartData | null,
  fundingHistogramData: AnalyticsChartData | null,
  fundingRoundTypesData: AnalyticsChartData | null,
  fundingAndGrantRounds: any[],
  revenueHistogramData: AnalyticsChartData | null,
  declaredRevenueHistogramData: AnalyticsChartData | null,
  employeeHistogramData: AnalyticsChartData | null,
  declaredEmployeeHistogramData: AnalyticsChartData | null,
  activeActorsHistogramData: AnalyticsChartData | null,
}

const dataFetchConfig = [
  {
    fetchFn: fetchEconomicReadinessKPIs,
    loadingState: 'isLoadingKPIs',
    dataState: 'kpis',
    transformer: transformKPIsResponseToKPIArray,
    errorMessage: 'Error fetching KPIs'
  },
  {
    fetchFn: fetchEconomicReadinessStatusHistogram,
    loadingState: 'isLoadingStatusHistogram',
    dataState: 'statusHistogramData',
    transformer: transformChartDataApiResponseToChartDataArray,
    errorMessage: 'Error fetching status histogram'
  },
  {
    fetchFn: fetchEconomicReadinessFundingHistogram,
    loadingState: 'isLoadingFundingHistogram',
    dataState: 'fundingHistogramData',
    transformer: transformChartDataApiResponseToChartDataArray,
    errorMessage: 'Error fetching funding histogram'
  },
  {
    fetchFn: fetchEconomicReadinessFundingRoundTypeHistogram,
    loadingState: 'isLoadingFundingRoundTypes',
    dataState: 'fundingRoundTypesData',
    transformer: transformChartDataApiResponseToChartDataArray,
    errorMessage: 'Error fetching funding round types histogram'
  },
  {
    fetchFn: fetchEconomicReadinessRevenueHistogram,
    loadingState: 'isLoadingRevenueHistogram',
    dataState: 'revenueHistogramData',
    transformer: transformChartDataApiResponseToChartDataArray,
    errorMessage: 'Error fetching revenue histogram'
  },
  {
    fetchFn: fetchEconomicReadinessDeclaredRevenueHistogram,
    loadingState: 'isLoadingDeclaredRevenueHistogram',
    dataState: 'declaredRevenueHistogramData',
    transformer: transformChartDataApiResponseToChartDataArray,
    errorMessage: 'Error fetching declared revenue histogram'
  },
  {
    fetchFn: fetchEconomicReadinessEmployeeHistogram,
    loadingState: 'isLoadingEmployeeHistogram',
    dataState: 'employeeHistogramData',
    transformer: transformChartDataApiResponseToChartDataArray,
    errorMessage: 'Error fetching employee histogram'
  },
  {
    fetchFn: fetchEconomicReadinessDeclaredEmployeeHistogram,
    loadingState: 'isLoadingDeclaredEmployeeHistogram',
    dataState: 'declaredEmployeeHistogramData',
    transformer: transformChartDataApiResponseToChartDataArray,
    errorMessage: 'Error fetching declared employee histogram'
  },
  {
    fetchFn: fetchFundingAndGrantRounds,
    loadingState: 'isLoadingFundingAndGrantRounds',
    dataState: 'fundingAndGrantRounds',
    transformer: transformFundingOrGrantRoundListDataToFundingOrGrantRoundArray,
    errorMessage: 'Error fetching funding and grant rounds'
  },
  {
    fetchFn: fetchEconomicReadinessActiveActorsHistogram,
    loadingState: 'isLoadingActiveActorsHistogram',
    dataState: 'activeActorsHistogramData',
    transformer: transformChartDataApiResponseToChartDataArray,
    errorMessage: 'Error fetching active actors histogram'
  }
]

export default defineComponent({
  name: "EconomicReadinessDashboard",
  data(): BaseComponentData {
    return {
      selectedPortfolio: null,
      selectedTimeframe: {
        label: new Date().getFullYear().toString(),
        value: {
          year: new Date().getFullYear(),
          quarter: null
        }
      },
      isLoadingKPIs: true,
      isLoadingStatusHistogram: true,
      isLoadingFundingHistogram: true,
      isLoadingFundingRoundTypes: true,
      isLoadingFundingAndGrantRounds: true,
      isLoadingRevenueHistogram: true,
      isLoadingDeclaredRevenueHistogram: true,
      isLoadingEmployeeHistogram: true,
      isLoadingDeclaredEmployeeHistogram: true,
      isLoadingActiveActorsHistogram: true,
      kpis: null,
      statusHistogramData: null,
      fundingHistogramData: null,
      fundingRoundTypesData: null,
      fundingAndGrantRounds: [],
      revenueHistogramData: null,
      declaredRevenueHistogramData: null,
      employeeHistogramData: null,
      declaredEmployeeHistogramData: null,
      activeActorsHistogramData: null
    }
  },
  computed: {
    title() {
      return "Economic Readiness Dashboard"
    },
    info() {
      return "Monitor Economic Readiness"
    },
    fundingAndGrantRoundsHeaders() {
      return [
        { key: 'logo', label: '', dataKey: 'logo', width: '50px' },
        { key: 'actorName', label: 'Name', dataKey: 'actorName', width: '25%' },
        { key: 'type', label: 'Type', dataKey: 'type', width: '15%' },
        { key: 'originalCapitalRaised', label: 'Amount', dataKey: 'originalCapitalRaised', width: '15%', align: 'right' as const },
        { key: 'originalCurrency', label: 'Currency', dataKey: 'originalCurrency', width: '10%' },
        { key: 'date', label: 'Date', dataKey: 'date', width: '15%' }
      ]
    }
  },
  mounted() {
    this.refreshAllData()
  },
  methods: {
    updatePortfolio(value) {
      this.selectedPortfolio = value
      this.refreshAllData()
    },
    updateTimeframe(value) {
      this.selectedTimeframe = value
      this.refreshAllData()
    },
    refreshAllData() {
      // Reset all data and loading states
      dataFetchConfig.forEach(config => {
        this[config.loadingState] = true
        this[config.dataState] = null
      })

      const filters = {
        portfolio: this.selectedPortfolio,
        year: this.selectedTimeframe.value.year,
        quarter: this.selectedTimeframe.value.quarter
      }

      // Handle each API call independently, if we work with a promise.all we might run into issues if one of the calls fails
      dataFetchConfig.forEach(config => {
        config.fetchFn(filters)
          .then(response => {
            this[config.dataState] = config.transformer(response)
          })
          .catch(error => {
            console.error(config.errorMessage + ':', error)
            this[config.dataState] = null
          })
          .finally(() => {
            this[config.loadingState] = false
          })
      })
    },
    formatDate(date: string) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    handleRowClick(row: any) {
      if (row.elasticId) {
        window.open(`/actors/${row.elasticId}`, '_blank')
      }
    }
  },
  components: {
    LineChart,
    Loading,
    TimeframeDropdown,
    SimplePortfolioDropdown,
    FacetCountCard,
    BarChart,
    DataTable,
    Avatar,
    ChartContainer
  }
})
</script>

<style scoped lang="scss">
@import "../../../../scss/variables.scss";

.economic-readiness-dashboard-body {
  max-width: 1200px;
  min-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  overflow-y: auto;
}

.economic-readiness-kpi-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: flex-start;
  margin-bottom: 2rem;
}

.economic-readiness-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.economic-readiness-chart-container {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.status-histogram-chart,
.funding-histogram-chart,
.funding-round-types-chart {
  width: 100%;
  height: 400px;
}

.economic-readiness-empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #666;
  font-size: 1.1rem;
  font-style: italic;
}

.chart-title {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 1.2rem;
}
</style>
